import { useQuery } from '@apollo/client'
import { Product } from '@danone-global/ct/interfaces'
import Price from '@danone-global/internal/react/components/price'
import { useChannelId } from '@danone-global/internal/react/modules/stock'
import {
  analytics,
  ANALYTICS_CATEGORY_LIMIT,
  capitalizeFirstLetter,
  limitArrayCharacters,
  useCore,
} from '@danone-global/internal/react/core'
import { makeStyles } from '@material-ui/styles'
import * as Sentry from '@sentry/react'
import clsx from 'clsx'
import React, { useCallback, useState } from 'react'

import { GET_PRICE_QUERY } from './price.graphql'

export interface Props {
  sku: string | number
  align?: string
  analytics?: boolean
  channel?: string
}

export const useStyles = makeStyles(
  {
    root: {
      display: 'flex',
    },

    alignLeft: {
      textAlign: 'left',
    },

    alignCenter: {
      textAlign: 'center',
    },

    alignRight: {
      textAlign: 'right',
    },
  },
  { name: 'WidgetPrice' },
)

export const WidgetPrice: React.FC<Props> = ({
  sku: providedSku,
  analytics: withAnalytics = true,
  align = 'right',
  channel: providedChannelId = '',
}) => {
  const { config, features, localeConfig } = useCore()
  const classes = useStyles()
  const sendAnalyticsRef = React.useRef(false)
  const [state, setState] = React.useState({
    price: null,
    additionalPrice: null,
  })
  const [priceUnit, setPriceUnit] = useState({
    contentquantity: null,
    measurementUnit: null
  })

  // Force sku to string
  const sku = React.useMemo(() => `${providedSku}`, [providedSku])

  // Retrieve channel id key for price
  let channelId = useChannelId(providedChannelId)
  channelId =  providedChannelId ? channelId : undefined

  const { data, refetch } = useQuery<{ product: Product }>(GET_PRICE_QUERY, {
    variables: {
      sku,
      channelId,
      currency: localeConfig.currency,
      country: localeConfig.country,
      locale: localeConfig.locale,
    },
  })

  const isPublished = data?.product?.masterData?.published

  const updateChannelIdSelected = useCallback(
      (event: Event & { detail: { channelId: string } }) => {
        refetch({
          sku,
          channelId: event.detail.channelId ? event.detail.channelId : undefined,
          currency: localeConfig.currency,
          country: localeConfig.country,
          locale: localeConfig.locale,
        })
      },
      [localeConfig, refetch, sku],
    )

      React.useEffect(() => {
          if (window) {
            window.addEventListener(
              `dtc-send-price-channel-${sku}`,
              updateChannelIdSelected,
            )
            return () => {
              window.removeEventListener(
                `dtc-send-price-channel-${sku}`,
                updateChannelIdSelected,
              )
            }
          }
        }, [sku, updateChannelIdSelected])

  React.useEffect(() => {
    if (data?.product) {
      const price =
        data?.product.masterData.current.variant?.price?.discounted?.value ||
        data?.product.masterData.current.variant?.price?.value

      setState({
        price,
        additionalPrice: data?.product.masterData.current.variant?.price
          ?.discounted?.value
          ? data?.product.masterData.current.variant?.price?.value
          : null,
      })

      const quantityUnit = data?.product?.masterData?.current?.variant.attributesRaw?.filter(item => item.name === 'contentquantity').map(quantity => quantity.value).join('')
      const measurement = data?.product?.masterData?.current?.variant.attributesRaw?.filter(item => item.name === 'measurementUnit').map(unit => unit.value.label).join('')

      setPriceUnit({contentquantity: Number(quantityUnit),measurementUnit: measurement})

    }
  }, [data])

  // If there is no money (product not found for example) then render nothing and if product is unpublished it will not be shown
  if (!state.price || (data?.product && !isPublished)) {
    return null
  }

  return (
    <div
      className={clsx('dtc-widget-price__root', classes.root)}
      data-testid={`widget-price-${sku}`}
    >
      <Price
        {...state.price}
        additionalPrice={state.additionalPrice}
        className={classes[`align${capitalizeFirstLetter(align)}`]}
        inParts={features.showPriceInParts}
        showAsterisk={features.showAsteriskByPrice}
        contentQuantity={priceUnit?.contentquantity}
        measurementUnit={priceUnit?.measurementUnit}
      />
    </div>
  )
}

export default Sentry.withProfiler(WidgetPrice, { name: 'WidgetPrice' })
